import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, 
  Quote, 
  Heart, 
  ArrowRight,
  Loader2,
  Share2,
  Facebook,
  Linkedin,
  Mail,
  MessageSquare,
  Instagram,
  Copy,
  Check
} from 'lucide-react';
import {
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton
} from 'react-share';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SoulFoodSharePage() {
  const { soulFoodId } = useParams();
  const [soulFood, setSoulFood] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    fetchSoulFood();
  }, [soulFoodId]);

  const fetchSoulFood = async () => {
    try {
      // Use the current endpoint to get the soul food
      const response = await axios.get(`${API_URL}/api/soul-food/${soulFoodId}`);
      setSoulFood(response.data.soul_food);
    } catch (err) {
      console.error('Error fetching Soul Food:', err);
      setError('This Soul Food could not be found.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getShareText = () => {
    if (!soulFood) return '';
    return `"${soulFood.quote_or_scripture}"\n— ${soulFood.source}\n\n#SisterDiamonds #${soulFood.theme?.replace(/\s+/g, '') || 'Inspiration'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-white" />
      </div>
    );
  }

  if (error || !soulFood) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <Sparkles className="h-12 w-12 mx-auto mb-4 text-purple-300" />
            <h1 className="text-xl font-semibold text-gray-800 mb-2">Soul Food Not Found</h1>
            <p className="text-gray-600 mb-6">This inspirational message may have been moved or is no longer available.</p>
            <Link to="/">
              <Button className="bg-purple-600 hover:bg-purple-700">
                Discover More Inspiration
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <img 
              src="/icons/icon-72x72.png" 
              alt="Sister Diamonds" 
              className="h-10 w-10 rounded-lg"
            />
            <span className="text-xl font-bold">Sister Diamonds</span>
          </Link>
          <Badge className="bg-white/20 text-white border-white/30">
            Soul Food
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pb-12">
        <div className="max-w-2xl mx-auto">
          {/* Soul Food Card */}
          <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0 overflow-hidden">
            <CardContent className="p-8 md:p-12">
              {/* Theme Badge */}
              {soulFood.theme && (
                <Badge className="mb-6 bg-purple-100 text-purple-700 border-purple-200">
                  <Sparkles className="h-3 w-3 mr-1" />
                  {soulFood.theme.charAt(0).toUpperCase() + soulFood.theme.slice(1)}
                </Badge>
              )}

              {/* Quote */}
              <div className="relative">
                <Quote className="absolute -left-2 -top-2 h-8 w-8 text-purple-200 rotate-180" />
                <blockquote className="text-2xl md:text-3xl font-serif text-gray-800 leading-relaxed pl-8 pr-4">
                  {soulFood.quote_or_scripture}
                </blockquote>
              </div>

              {/* Source */}
              <p className="mt-6 text-lg text-purple-600 font-medium text-right">
                — {soulFood.source}
              </p>

              {/* Reflection Preview */}
              {soulFood.reflection && (
                <div className="mt-8 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-sm text-gray-600 italic line-clamp-3">
                    {soulFood.reflection}
                  </p>
                </div>
              )}

              {/* Engagement Stats */}
              <div className="mt-8 flex items-center justify-center gap-6 text-gray-500">
                <span className="flex items-center gap-1">
                  <Heart className="h-4 w-4 text-pink-500" />
                  {soulFood.likes?.length || 0} inspired
                </span>
              </div>

              {/* Share Section */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-center text-sm text-gray-500 mb-4">Share this inspiration</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <FacebookShareButton
                    url={window.location.href}
                    quote={getShareText()}
                  >
                    <div className="p-3 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer">
                      <Facebook className="h-5 w-5 text-blue-600" />
                    </div>
                  </FacebookShareButton>

                  <a
                    href={`https://www.instagram.com/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-colors cursor-pointer"
                    title="Share on Instagram (save image first)"
                  >
                    <Instagram className="h-5 w-5 text-pink-600" />
                  </a>

                  <LinkedinShareButton
                    url={window.location.href}
                    title="Soul Food from Sister Diamonds"
                    summary={getShareText()}
                  >
                    <div className="p-3 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer">
                      <Linkedin className="h-5 w-5 text-blue-700" />
                    </div>
                  </LinkedinShareButton>

                  <WhatsappShareButton
                    url={window.location.href}
                    title={getShareText()}
                  >
                    <div className="p-3 rounded-full bg-green-50 hover:bg-green-100 transition-colors cursor-pointer">
                      <MessageSquare className="h-5 w-5 text-green-600" />
                    </div>
                  </WhatsappShareButton>

                  <EmailShareButton
                    url={window.location.href}
                    subject="Soul Food from Sister Diamonds"
                    body={getShareText()}
                  >
                    <div className="p-3 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                      <Mail className="h-5 w-5 text-gray-600" />
                    </div>
                  </EmailShareButton>

                  <button
                    onClick={handleCopyLink}
                    className="p-3 rounded-full bg-purple-50 hover:bg-purple-100 transition-colors cursor-pointer"
                  >
                    {copiedLink ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5 text-purple-600" />
                    )}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Card */}
          <Card className="mt-6 bg-white/90 backdrop-blur-sm border-0">
            <CardContent className="p-6 text-center">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Want more Soul Food?
              </h2>
              <p className="text-gray-600 mb-4">
                Join Sister Diamonds and receive weekly inspiration, reflections, and community support.
              </p>
              <Link to="/inner-circle">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white">
                  Join Our Community
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center text-white/80 text-sm">
            <p>© {new Date().getFullYear()} Sister Diamonds. All rights reserved.</p>
            <p className="mt-1">Empowering extraordinary women everywhere.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
